import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
	BlockOutlined,
	SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import LockOpenIcon from '@mui/icons-material/LockOpen';

import ContextMenu from 'src/components/common/context_menu/ContextMenu';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IUpdateUserRequest,
	IUserTableActionsProps,
} from 'src/features/user/domain/types';
import {
	updateUser,
	USER_LOADING_KEY,
} from 'src/features/user/store/userThunks';
import { AppDispatch } from 'src/store';

import { ResetUserPasswordModal } from './ResetUserPasswordModal';

export const UserTableActions: React.FC<IUserTableActionsProps> = ({
	selectedRow,
	anchorElement,
	clearAnchorElement,
	OnActionHit,
	onClose,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] =
		useState<boolean>(false);
	const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
		useState<boolean>(false);

	const menuVisible = Boolean(anchorElement);

	const handleUpdateUser = async (request: IUpdateUserRequest) => {
		dispatch(
			updateUser({
				request: request,
				userId: selectedRow.id,
				onSuccess: () => {
					snackbar('User updated successfully', 'success');
					if (request.status) setIsConfirmationModalVisible(false);
					if (request.password) setIsResetPasswordModalVisible(false);
					OnActionHit(!!request.password); // preserve table filters on password change
				},
			}),
		);
	};

	return (
		<>
			<ContextMenu
				open={menuVisible}
				onClose={onClose}
				anchorElement={anchorElement}
				clearAnchorElement={clearAnchorElement}
				items={[
					{
						icon: <LockOpenIcon />,
						label: 'Reset Password',
						onClick: () => {
							setIsResetPasswordModalVisible(true);
						},
					},

					{
						icon:
							selectedRow.status === 'deactivated' ? (
								<SettingsBackupRestoreOutlined />
							) : (
								<BlockOutlined />
							),

						label:
							selectedRow.status === 'deactivated'
								? 'Activate user'
								: 'Deactivate user',

						onClick: () => setIsConfirmationModalVisible(true),
					},
				]}
			/>

			<ResetUserPasswordModal
				open={isResetPasswordModalVisible}
				user={selectedRow}
				onClose={() => setIsResetPasswordModalVisible(false)}
				onSubmit={(data) =>
					handleUpdateUser({ password: data.new_password })
				}
			/>

			<ConfirmationModal
				title={`Are you sure you want to ${
					selectedRow.status === 'deactivated'
						? 'activate'
						: 'deactivate'
				} the user?`}
				modalContent={
					selectedRow.status === 'deactivated'
						? 'The user will be activated and their permissions will be restored.'
						: 'The user will be deactivated and their permissions will be revoked.'
				}
				modalOpen={isConfirmationModalVisible}
				handleYes={() =>
					handleUpdateUser({
						status:
							selectedRow.status === 'deactivated'
								? 'active'
								: 'deactivated',
					})
				}
				loadingKey={USER_LOADING_KEY}
				handleNo={() => setIsConfirmationModalVisible(false)}
				color={
					selectedRow.status === 'deactivated' ? 'green' : undefined
				}
			/>
		</>
	);
};
